import { type TPlacement, type TPlacementAxis } from './resolve-placement';
import { type TStyleDeclaration } from './set-style';

/**
 * The space kept clear between a popover and the viewport edge. Matches legacy
 * `@atlaskit/popper`'s `preventOverflow` padding of `5`.
 */
export const VIEWPORT_PADDING = '5px';

/**
 * The upper bound of the default placement-axis floor.
 *
 * A capped popover never overflows, so it never flips. This floor makes the
 * margin box overflow a too-small cell, so `position-try-fallbacks` fires. That
 * is its only job, so the JavaScript fallback, which has no cell to overflow,
 * never writes it. See `getPlacementAxisFloor`.
 *
 * 150px is the value the flip was measured with, not a design constant
 * (`notes/plans/should-fit-viewport.md`). It is not a `space.*` token on
 * purpose: theming it would move the flip threshold, and so change which side a
 * popover lands on.
 */
export const FALLBACK_MINIMUM_MAIN_AXIS_SIZE = '150px';

/**
 * Which way the POPOVER's inline axis runs; `'vertical'` covers the `vertical-*`
 * and `sideways-*` writing modes. Measured rather than assumed, because every
 * logical property this module writes resolves against the popover's own writing
 * mode, and so must the viewport unit paired with it.
 */
export type TPopoverWritingMode = 'horizontal' | 'vertical';

/**
 * The dynamic viewport unit for one of the popover's axes. Not `dvi` / `dvb`:
 * css-values-4 defines those against the ROOT element's writing mode, while the
 * `max-{axis}-size` they are written to follows the popover's, so a popover in a
 * `vertical-rl` subtree of a horizontal document would be capped by the wrong
 * dimension.
 */
function viewportUnit({
	axis,
	writingMode,
}: {
	axis: TPlacementAxis;
	writingMode: TPopoverWritingMode;
}): string {
	// The inline axis is horizontal in a horizontal writing mode. The block axis is
	// horizontal in a vertical one.
	const isHorizontalAxis = (axis === 'inline') === (writingMode === 'horizontal');
	return isHorizontalAxis ? 'dvw' : 'dvh';
}

/**
 * How one axis of an anchored popover is sized. Both axes take these four values
 * under identical rules.
 *
 * - `'content'`: sized by its content. Named after `flex-basis: content`; NOT
 *   `'auto'`, whose CSS meaning for a positioned box is shrink-to-fit, the very
 *   behaviour rule 4 removes.
 * - `'match-anchor'`: exactly the anchor's size on this axis.
 * - `'min-anchor'`: at least the anchor's size on this axis, free to grow.
 * - `'max-available'`: at most the space between the anchor and the viewport
 *   edge, content-sized below that. Capped by the `position-area` cell on the
 *   placement axis and by the viewport on the cross axis (a `span-*` cell is
 *   narrower than the viewport, so capping to it would wrap content that fits on
 *   screen).
 *
 * Four rules apply on top:
 *
 * 1. Fitting one axis fills a `'content'` other axis with `'max-available'` too.
 *    An explicit value is never overridden, so caps stay strictly per-axis.
 * 2. On the CSS path, fitting either axis floors the placement axis, because
 *    flipping is a whole-popover outcome. See `getPlacementAxisFloor`.
 * 3. The viewport cap is unconditional, so a non-fitting axis still cannot
 *    overhang the screen. `'max-available'` and `'content'` are therefore
 *    indistinguishable on the cross axis.
 * 4. A non-anchor-relative inline axis gets `inline-size: max-content`. Under
 *    `position-area` the containing block is the cell, so a CSS `auto` inline
 *    size shrink-to-fits a too-narrow cell and WRAPS into it, never overflowing
 *    and so never sliding to the roomier side. The caps still beat it, because a
 *    max beats a definite size. Not needed on the block axis, which does not
 *    wrap, nor on the JavaScript fallback, whose containing block is the
 *    viewport. Consumers opt out with `shouldPreserveInlineSize`.
 *
 * See `notes/decisions/fit-available-space.md`.
 */
export type TPopoverAxisSize = 'content' | 'match-anchor' | 'min-anchor' | 'max-available';

/**
 * A size property this module writes. Narrowed rather than `string`, so a typo
 * cannot reach `setStyle` as a property name that the browser silently ignores.
 */
export type TPopoverSizeProperty =
	| `${TPlacementAxis}-size`
	| `min-${TPlacementAxis}-size`
	| `max-${TPlacementAxis}-size`;

/**
 * The anchor's size along each axis, as a CSS length. `null` when nothing needs
 * it, so the measurement is skipped.
 *
 * The CSS path uses the `self-` keywords, whose axes are the POPOVER's, so each
 * value matches the axis of the property it is set on (`anchor-size(inline)` and
 * `anchor-size(width)` are both silently wrong in a vertical writing mode). The
 * JavaScript fallback uses a measured pixel length, because `anchor-size()` needs
 * a `position-anchor` that path never sets. See
 * `notes/decisions/width-from-anchor-floors.md`.
 */
export type TAnchorSizeValues = Record<TPlacementAxis, string | null>;

function getCrossAxis({ axis }: { axis: TPlacementAxis }): TPlacementAxis {
	return axis === 'block' ? 'inline' : 'block';
}

/**
 * The whole viewport, less the reserved padding on both sides. Rule 3's backstop.
 */
function viewportCap({
	axis,
	writingMode,
}: {
	axis: TPlacementAxis;
	writingMode: TPopoverWritingMode;
}): string {
	return `calc(100${viewportUnit({ axis, writingMode })} - 2 * ${VIEWPORT_PADDING})`;
}

/**
 * The position-area cell cap, for the placement axis only. The host carries
 * `position-area`, so per css-anchor-position-1 the cell is its containing block:
 * `100%` is the anchor edge to the viewport edge, and it follows whichever side
 * `position-try-fallbacks` settles on. Percentages do not subtract margins, so
 * the gap is subtracted here.
 *
 * Meaningless on the JavaScript fallback, whose containing block is the viewport,
 * so that path gets `viewportCap` on both axes.
 */
function cellCap({ gap }: { gap: string }): string {
	return `calc(100% - ${VIEWPORT_PADDING} - ${gap})`;
}

/**
 * The anchor term of the placement-axis floor, or `null` for none. It is the only
 * part of the floor that is ever clamped to a cap.
 *
 * `'min-anchor'` always gets an anchor floor. `'match-anchor'` gets one only where
 * it can flip: its definite size alone gives way to the cap, so the popover would
 * shrink below the anchor instead of flipping.
 *
 * It is clamped only where it cannot flip, on purpose:
 *
 * - With no flip, an unclamped floor beats `max-{axis}-size` (CSS applies the
 *   `min` last), so the viewport backstop would do nothing.
 * - With a flip, a clamp would stop the floor exceeding the cell cap, and that
 *   overflow is what drives the flip.
 */
function getPlacementAxisAnchorFloor({
	placementAxis,
	placementAxisSize,
	anchorValue,
	canFlip,
	writingMode,
}: {
	placementAxis: TPlacementAxis;
	placementAxisSize: TPopoverAxisSize;
	anchorValue: string | null;
	canFlip: boolean;
	writingMode: TPopoverWritingMode;
}): string | null {
	const hasAnchorFloor =
		placementAxisSize === 'min-anchor' || (placementAxisSize === 'match-anchor' && canFlip);

	if (!hasAnchorFloor || anchorValue === null) {
		return null;
	}

	if (canFlip) {
		return anchorValue;
	}

	return `min(${anchorValue}, ${viewportCap({ axis: placementAxis, writingMode })})`;
}

/**
 * The default flip floor, clamped so that SOME cell can always hold it.
 *
 * The two cells on either side of the anchor add up to the viewport less the
 * anchor, so the roomier one is at least half of that. A floor whose margin box
 * fits inside that half is guaranteed a cell somewhere in the fallback chain.
 * Unclamped, a short viewport with a centred anchor has NO cell that holds
 * 150px: every try-fallback overflows, the browser reverts to the base position,
 * and the floor pushes the popover past the viewport edge.
 *
 * This is NOT the `min(floor, cellCap)` that suppresses flipping: it is relative
 * to the VIEWPORT, so a small base cell still overflows it. `max(0px, …)` stops
 * the floor going negative, for example for an anchor larger than the viewport.
 */
function getDefaultPlacementAxisFloor({
	placementAxis,
	anchorValue,
	gap,
	writingMode,
}: {
	placementAxis: TPlacementAxis;
	anchorValue: string | null;
	gap: string;
	writingMode: TPopoverWritingMode;
}): string {
	if (anchorValue === null) {
		return FALLBACK_MINIMUM_MAIN_AXIS_SIZE;
	}
	// The viewport term and the anchor term must measure the SAME axis; see
	// `viewportUnit`.
	const viewport = `100${viewportUnit({ axis: placementAxis, writingMode })}`;
	const halfOfRemainingViewport = `calc((${viewport} - ${anchorValue}) / 2 - ${gap} - ${VIEWPORT_PADDING})`;
	return `min(${FALLBACK_MINIMUM_MAIN_AXIS_SIZE}, max(0px, ${halfOfRemainingViewport}))`;
}

/**
 * The placement-axis floor, or `null` for none. Explicit minimums COMPOSE, the
 * default does not:
 *
 * - `placement.minSize` and the anchor term are both explicit requests, so they
 *   compose as `max()`. As intended, `minSize: 0` does not remove an anchor
 *   floor, because `max(0px, anchor)` IS the anchor.
 * - The default floor applies only when neither is set and the popover can flip.
 *   It never composes: in a `max()` it would only win by overriding a size the
 *   consumer asked for, and the anchor term drives the flip on its own.
 */
function getPlacementAxisFloor({
	minSize,
	placementAxis,
	placementAxisSize,
	anchorValue,
	gap,
	canFlip,
	writingMode,
}: {
	minSize: string | undefined;
	placementAxis: TPlacementAxis;
	placementAxisSize: TPopoverAxisSize;
	anchorValue: string | null;
	gap: string;
	canFlip: boolean;
	writingMode: TPopoverWritingMode;
}): string | null {
	const anchorFloor = getPlacementAxisAnchorFloor({
		placementAxis,
		placementAxisSize,
		anchorValue,
		canFlip,
		writingMode,
	});

	if (minSize !== undefined) {
		return anchorFloor === null ? minSize : `max(${minSize}, ${anchorFloor})`;
	}

	if (anchorFloor !== null) {
		return anchorFloor;
	}

	if (!canFlip) {
		return null;
	}

	return getDefaultPlacementAxisFloor({ placementAxis, anchorValue, gap, writingMode });
}

/**
 * The whole size recipe for an anchored popover, in one place. Pure, so the truth
 * table is unit-testable without a browser, and every size property on the
 * popover host has exactly one writer.
 *
 * While anything is fitting on the CSS path, the placement-axis floor is never
 * clamped to a cap. Its whole job is to exceed the CELL cap: overflow detection
 * reads the margin box AFTER the clamp (css-anchor-position-1 §6.5), so
 * `min(floor, cap)` suppresses `position-try-fallbacks` entirely. The cross-axis
 * floor is always clamped, because there it is a size contract rather than a flip
 * driver.
 *
 * See `notes/decisions/fit-available-space.md` for the full recipe and the
 * measured evidence.
 */
export function getAnchoredPopoverSizeDeclarations({
	placement,
	inlineSize,
	blockSize,
	isUsingCssAnchorPositioning,
	anchorSize,
	writingMode,
	shouldPreserveInlineSize = false,
}: {
	/**
	 * Supplies the axis, the gap and `minSize`.
	 */
	placement: TPlacement;
	/**
	 * The requested size, before rule 1 fills a `'content'` axis.
	 */
	inlineSize: TPopoverAxisSize;
	/**
	 * The requested size, before rule 1 fills a `'content'` axis.
	 */
	blockSize: TPopoverAxisSize;
	/**
	 * `false` on the JavaScript fallback path, which has no position-area cell, so
	 * both axes get the viewport cap and no flip floor is written.
	 */
	isUsingCssAnchorPositioning: boolean;
	/**
	 * Already resolved for the active path. See `TAnchorSizeValues`.
	 */
	anchorSize: TAnchorSizeValues;
	/**
	 * Picks the physical viewport unit for each logical axis. See `viewportUnit`.
	 */
	writingMode: TPopoverWritingMode;
	/**
	 * `true` skips rule 4, for an element the consumer owns and may have given a
	 * `width` that an inline `max-content` would override.
	 */
	shouldPreserveInlineSize?: boolean;
}): TStyleDeclaration<TPopoverSizeProperty>[] {
	const placementAxis = placement.axis;
	const crossAxis = getCrossAxis({ axis: placementAxis });

	// Rule 1. In effect this feeds only the placement-axis cap: every other check
	// below treats `'content'` and `'max-available'` the same.
	const size: Record<TPlacementAxis, TPopoverAxisSize> = {
		inline:
			inlineSize === 'content' && blockSize === 'max-available' ? 'max-available' : inlineSize,
		block: blockSize === 'content' && inlineSize === 'max-available' ? 'max-available' : blockSize,
	};

	// Rule 2 reads the requested values, not `size`: flipping is a whole-popover
	// outcome, so a fit request on either axis turns the floor on.
	const isFitting = inlineSize === 'max-available' || blockSize === 'max-available';

	// The floor drives the flip only on the CSS path. On the JavaScript fallback a
	// floor only inflates the measured size that the side is picked from, so it
	// would flip a popover that fits.
	const canFlip = isFitting && isUsingCssAnchorPositioning;

	const declarations: TStyleDeclaration<TPopoverSizeProperty>[] = [
		// Rule 3: both caps are always written. Only the placement axis can take the
		// cell cap.
		{
			property: `max-${placementAxis}-size`,
			value:
				size[placementAxis] === 'max-available' && isUsingCssAnchorPositioning
					? cellCap({ gap: placement.offset.gap })
					: viewportCap({ axis: placementAxis, writingMode }),
		},
		{ property: `max-${crossAxis}-size`, value: viewportCap({ axis: crossAxis, writingMode }) },
	];

	// Rule 4. Only for a non-anchor-relative inline axis, so `'match-anchor'` below
	// stays the one writer of `inline-size` when it applies. CSS path only: the
	// fallback's containing block is the viewport, where shrink-to-fit already IS
	// the natural width for anything that fits on screen.
	if (
		isUsingCssAnchorPositioning &&
		!shouldPreserveInlineSize &&
		(size.inline === 'content' || size.inline === 'max-available')
	) {
		declarations.push({ property: 'inline-size', value: 'max-content' });
	}

	// `'match-anchor'` is a definite size. A max beats a definite size, so the caps
	// above still win.
	[placementAxis, crossAxis].forEach((axis) => {
		const anchorValue = anchorSize[axis];
		if (size[axis] === 'match-anchor' && anchorValue !== null) {
			declarations.push({ property: `${axis}-size`, value: anchorValue });
		}
	});

	const placementAxisFloor = getPlacementAxisFloor({
		minSize: placement.minSize,
		placementAxis,
		placementAxisSize: size[placementAxis],
		anchorValue: anchorSize[placementAxis],
		gap: placement.offset.gap,
		canFlip,
		writingMode,
	});
	if (placementAxisFloor !== null) {
		declarations.push({ property: `min-${placementAxis}-size`, value: placementAxisFloor });
	}

	// `'min-anchor'` on the cross axis is a size contract, not a flip driver, so it
	// is clamped to the viewport cap.
	const crossAxisAnchorValue = anchorSize[crossAxis];
	if (size[crossAxis] === 'min-anchor' && crossAxisAnchorValue !== null) {
		declarations.push({
			property: `min-${crossAxis}-size`,
			value: `min(${crossAxisAnchorValue}, ${viewportCap({ axis: crossAxis, writingMode })})`,
		});
	}

	return declarations;
}
